import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  ExpandLess,
  ExpandMore,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { UserRole } from '../../types';
import { DateUtils } from '../../utils/date-utils';
import { useGetActiveManufacturersQuery } from '../manufacturer/manufacturers-api-slice';
import { selectCurrentUser } from '../user/user-slice';
import BOMHardwareModal, { BOMHardwareModalMode } from './bom-hardware-modal';
import BulkUpdateModal from './bulk-update-modal';
import BundleCostModal, { BundleCostModalMode } from './bundle-cost-modal';
import {
  useGetAllMaterialGroupOptionsQuery,
  useGetBOMHardwareQuery,
  useGetBundleCostsQuery,
  useGetVersionByIdQuery,
  useUpdatePriceSettingsMutation,
} from './price-management-api-slice';
import PublishVersionModal from './publish-version-modal';
import { BOMHardware, BundleCost, PriceVersion } from './types';

type TabValue = 'bundle-costs' | 'hardware-costs';

/**
 * Material Pricing Calculator Page
 * Displays bundle costs and hardware costs for a selected version with tabs, version details, and pricing configuration
 */
const ProductPricingCalculatorPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const currentUser = useAppSelector(selectCurrentUser);
  const isAdmin = currentUser?.role === UserRole.ADMINISTRATOR;
  const canEditPricing =
    currentUser?.role === UserRole.ADMINISTRATOR || currentUser?.role === UserRole.SUPERVISOR;

  // Get version ID from URL params
  const versionIdParam = searchParams.get('versionId');
  const versionId = versionIdParam ? parseInt(versionIdParam, 10) : undefined;
  const mode = searchParams.get('mode') || 'view';

  // Fetch version details
  const {
    data: version,
    isLoading: versionLoading,
    error: versionError,
  } = useGetVersionByIdQuery(versionId!, {
    skip: !versionId,
  });

  // Fetch manufacturers for admin display
  const { data: manufacturers = [] } = useGetActiveManufacturersQuery(undefined, {
    skip: !isAdmin,
  });

  // State
  const [activeTab, setActiveTab] = useState<TabValue>('bundle-costs');
  const [pricingConfigExpanded, setPricingConfigExpanded] = useState(true);
  const [laborRate, setLaborRate] = useState<number>(0);
  const [productionFeetPerHour, setProductionFeetPerHour] = useState<number>(0);
  const [bundleCostModalOpen, setBundleCostModalOpen] = useState(false);
  const [bundleCostModalMode, setBundleCostModalMode] = useState<BundleCostModalMode>('add');
  const [selectedBundleCost, setSelectedBundleCost] = useState<BundleCost | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const [bomHardwareModalOpen, setBOMHardwareModalOpen] = useState(false);
  const [bomHardwareModalMode, setBOMHardwareModalMode] = useState<BOMHardwareModalMode>('add');
  const [selectedBOMHardware, setSelectedBOMHardware] = useState<BOMHardware | null>(null);

  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Read success message from router state (e.g. after creating a new version)
  useEffect(() => {
    const state = location.state as { successMessage?: string } | null;
    if (state?.successMessage) {
      setSuccessMessage(state.successMessage);
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Price settings mutation
  const [updatePriceSettings, { isLoading: isUpdatingPriceSettings }] =
    useUpdatePriceSettingsMutation();

  // Update local state when version loads
  React.useEffect(() => {
    if (version) {
      setLaborRate(version.fullyBurdenedLaborRate);
      setProductionFeetPerHour(version.productionFeetPerHour);
    }
  }, [version]);

  // Handle price settings update
  const handleApplyPriceSettings = async () => {
    if (!versionId || !version) return;

    try {
      await updatePriceSettings({
        versionId,
        fullyBurdenedLaborRate: laborRate,
        productionFeetPerHour,
      }).unwrap();
      // Version will be refetched automatically due to cache invalidation
    } catch (error) {
      console.error('Error updating price settings:', error);
      setErrorMessage('Failed to save pricing configuration. Please try again.');
    }
  };

  // Fetch material group options (for bulk update modal)
  const { data: materialGroupOptions = [] } = useGetAllMaterialGroupOptionsQuery();

  // Fetch bundle costs (always — needed for bulk update preview)
  const { data: bundleCosts = [], isLoading: bundleCostsLoading } = useGetBundleCostsQuery(
    { versionId: versionId! },
    { skip: !versionId }
  );

  // Group bundle costs by material group, sorted alphabetically
  const groupedBundleCosts = useMemo(() => {
    const groups = new Map<string, BundleCost[]>();
    bundleCosts.forEach(bc => {
      const group = bc.materialGroup || 'Other';
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group)!.push(bc);
    });
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [bundleCosts]);

  // Initialize all groups as expanded when data loads
  useEffect(() => {
    if (groupedBundleCosts.length > 0) {
      setExpandedGroups(new Set(groupedBundleCosts.map(([name]) => name)));
    }
  }, [groupedBundleCosts]);

  const handleExpandAll = () =>
    setExpandedGroups(new Set(groupedBundleCosts.map(([name]) => name)));
  const handleCollapseAll = () => setExpandedGroups(new Set());
  const toggleGroup = (name: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  // Fetch BOM hardware (always — needed for bulk update preview)
  const { data: bomHardware = [], isLoading: bomHardwareLoading } = useGetBOMHardwareQuery(
    versionId!,
    { skip: !versionId }
  );

  // Get manufacturer name
  const manufacturerName = useMemo(() => {
    if (!version) return '';
    if (isAdmin) {
      const manufacturer = manufacturers.find(m => m.id === version.manufacturerId);
      return manufacturer?.licenseeName || '';
    }
    return currentUser?.manufacturer || '';
  }, [version, manufacturers, isAdmin, currentUser]);

  // Format currency
  const formatCurrency = (value: number | undefined): string => {
    if (value === undefined || value === null) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Format date
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '—';
    return DateUtils.toDisplayDate(dateString) || '—';
  };

  // Format date/time
  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Calculate price change
  const calculateChange = (current: number, previous?: number) => {
    if (!previous) return { type: 'new', value: 0, percent: 0 };
    const diff = current - previous;
    const percent = previous !== 0 ? (diff / previous) * 100 : 0;
    return {
      type: diff > 0 ? 'increase' : diff < 0 ? 'decrease' : 'no-change',
      value: Math.abs(diff),
      percent: Math.abs(percent),
    };
  };

  if (currentUser?.role === UserRole.DEALER) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Material Pricing Calculator
        </Typography>
        <Alert severity="error" sx={{ mt: 1 }}>
          <strong>Access Restricted</strong> — The Material Pricing Calculator is not available for
          Dealer accounts. Please contact your manufacturer representative for pricing information.
        </Alert>
      </Box>
    );
  }

  // Loading state
  if (versionLoading) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <Typography>Loading version details...</Typography>
      </Box>
    );
  }

  // Error state
  if (versionError || !versionId || !version) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Version Not Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The requested version could not be loaded.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/product-pricing-calculator')}>
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  const isDraft = version.status === 'Draft';
  const isReadOnly = !isDraft || mode === 'view' || !canEditPricing;
  const hasPublishableData = bundleCosts.length > 0 || bomHardware.length > 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Back button + Page Title */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() =>
          navigate('/product-pricing-calculator', {
            state: { publishedManufacturerId: version?.manufacturerId },
          })
        }
        sx={{ mb: 1 }}
      >
        Back to Version List
      </Button>
      <Typography variant="h4" component="h1" gutterBottom>
        Material Pricing Calculator
      </Typography>

      {/* Version Details and Pricing Configuration Header */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {/* Version Details Section */}
        <Paper elevation={1} sx={{ p: 2, flex: 1, minWidth: 300 }}>
          <Typography variant="h6" gutterBottom>
            Version Details
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="h5" component="span">
              Version {version.versionNumber}.0
            </Typography>
            <Chip
              label={version.status.toUpperCase()}
              size="small"
              color={isDraft ? 'warning' : 'success'}
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
          {isAdmin && manufacturerName && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Manufacturer:</strong> {manufacturerName}
            </Typography>
          )}
          <Box
            sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, fontSize: '0.875rem' }}
          >
            <Typography variant="body2" color="text.secondary">
              Created:
            </Typography>
            <Typography variant="body2">
              {formatDateTime(version.created)} by{' '}
              {version.createdByName || version.createdBy || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last Updated:
            </Typography>
            <Typography variant="body2">
              {version.updatedByName || version.updatedBy || 'N/A'} -{' '}
              {formatDateTime(version.updated)}
            </Typography>
            {version.changeDescription && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Notes:
                </Typography>
                <Typography variant="body2">{version.changeDescription}</Typography>
              </>
            )}
          </Box>
        </Paper>

        {/* Pricing Configuration Panel */}
        <Paper
          elevation={1}
          sx={{
            p: 2,
            flex: 1,
            minWidth: 300,
            backgroundColor: 'purple.50',
            border: '1px solid',
            borderColor: 'purple.200',
          }}
        >
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon />
              <Typography variant="h6">Pricing Configuration</Typography>
            </Box>
            <Button
              size="small"
              onClick={() => setPricingConfigExpanded(!pricingConfigExpanded)}
              endIcon={pricingConfigExpanded ? <ExpandLess /> : <ExpandMore />}
            >
              {pricingConfigExpanded ? 'Collapse' : 'Expand'}
            </Button>
          </Box>
          <Collapse in={pricingConfigExpanded}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Fully Burdened Labor Rate"
                type="number"
                value={laborRate}
                onChange={e => setLaborRate(parseFloat(e.target.value) || 0)}
                disabled={isReadOnly}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="$ per hour"
                size="small"
              />
              <TextField
                label="Production Feet/hour"
                type="number"
                value={productionFeetPerHour}
                onChange={e => setProductionFeetPerHour(parseFloat(e.target.value) || 0)}
                disabled={isReadOnly}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="feet per hour"
                size="small"
              />
              {isDraft && canEditPricing && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  sx={{ alignSelf: 'flex-start' }}
                  onClick={handleApplyPriceSettings}
                  disabled={isUpdatingPriceSettings || !versionId}
                >
                  {isUpdatingPriceSettings ? 'Applying...' : 'Apply Changes'}
                </Button>
              )}
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* Tabs and Action Buttons */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              minHeight: 48,
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
              '& .MuiTab-root': {
                fontSize: '1rem',
                fontWeight: 500,
                textTransform: 'none',
                minHeight: 48,
                padding: '12px 24px',
                '&.Mui-selected': {
                  fontWeight: 600,
                  color: 'primary.main',
                },
              },
            }}
          >
            <Tab label="Bundle Costs" value="bundle-costs" />
            <Tab label="Hardware Costs" value="hardware-costs" />
          </Tabs>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canEditPricing && (
              <Tooltip title={isDraft ? '' : 'Bulk updates only available for Draft versions'}>
                <span style={{ display: 'inline-flex' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setBulkUpdateModalOpen(true)}
                    disabled={!isDraft}
                  >
                    Bulk Update
                  </Button>
                </span>
              </Tooltip>
            )}
            {isDraft && canEditPricing && (
              <Tooltip
                title={
                  !hasPublishableData
                    ? 'Cannot publish: add at least one bundle cost or hardware cost before publishing.'
                    : 'After this version has been published it will go into immediate effect and be locked from any future updates.'
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<span>✓</span>}
                    onClick={() => setPublishModalOpen(true)}
                    disabled={!hasPublishableData}
                  >
                    Publish
                  </Button>
                </span>
              </Tooltip>
            )}
            <Button
              variant="outlined"
              size="small"
              disabled={!version?.manufacturerId}
              onClick={() =>
                navigate(`/product-size-price-totals?manufacturerId=${version?.manufacturerId}`)
              }
            >
              Price Totals
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={!version?.manufacturerId}
              onClick={() =>
                navigate(`/product-price-sheet?manufacturerId=${version?.manufacturerId}`)
              }
            >
              See Price Sheet
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={!version?.manufacturerId}
              onClick={() =>
                navigate(`/bom-specifications?manufacturerId=${version?.manufacturerId}`)
              }
            >
              BOM Specifications
            </Button>
          </Box>
        </Box>
        {/* Add button and Expand/Collapse buttons on same row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
            mb: 1,
          }}
        >
          {activeTab === 'bundle-costs' && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" onClick={handleExpandAll}>
                Expand All
              </Button>
              <Button size="small" variant="outlined" onClick={handleCollapseAll}>
                Collapse All
              </Button>
            </Box>
          )}
          {activeTab === 'hardware-costs' && <Box />}
          {isDraft && canEditPricing && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                if (activeTab === 'bundle-costs') {
                  setSelectedBundleCost(null);
                  setBundleCostModalMode('add');
                  setBundleCostModalOpen(true);
                } else {
                  setSelectedBOMHardware(null);
                  setBOMHardwareModalMode('add');
                  setBOMHardwareModalOpen(true);
                }
              }}
            >
              + Add {activeTab === 'bundle-costs' ? 'Bundle Cost' : 'Hardware Cost'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Grid */}
      <Paper elevation={1} sx={{ overflow: 'hidden' }}>
        {activeTab === 'bundle-costs' ? (
          bundleCostsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <Typography color="text.secondary">Loading bundle costs...</Typography>
            </Box>
          ) : groupedBundleCosts.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <Typography color="text.secondary">
                No bundle costs found.{' '}
                {isDraft && canEditPricing && 'Click "+ Add Bundle Cost" to get started.'}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 220, fontWeight: 600, bgcolor: 'grey.50', pl: 2 }}>
                      Product
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ width: 170, fontWeight: 600, bgcolor: 'grey.50' }}
                    >
                      Prev Bundle Cost
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ width: 170, fontWeight: 600, bgcolor: 'grey.50' }}
                    >
                      Current Bundle Cost
                    </TableCell>
                    <TableCell sx={{ width: 200, fontWeight: 600, bgcolor: 'grey.50' }}>
                      Change
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ width: 110, fontWeight: 600, bgcolor: 'grey.50' }}
                    >
                      Bundle Qty
                    </TableCell>
                    <TableCell sx={{ width: 130, fontWeight: 600, bgcolor: 'grey.50' }}>
                      Updated
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ width: 80, fontWeight: 600, bgcolor: 'grey.50' }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedBundleCosts.map(([groupName, items]) => {
                    const isExpanded = expandedGroups.has(groupName);
                    return (
                      <React.Fragment key={groupName}>
                        {/* Group Header Row */}
                        <TableRow
                          onClick={() => toggleGroup(groupName)}
                          sx={{
                            cursor: 'pointer',
                            bgcolor: 'primary.50',
                            borderTop: '2px solid',
                            borderColor: 'primary.100',
                            '&:hover': { bgcolor: 'primary.100' },
                          }}
                        >
                          <TableCell colSpan={7} sx={{ py: 1, px: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <IconButton size="small" sx={{ p: 0.25 }}>
                                {isExpanded ? (
                                  <ExpandLess fontSize="small" />
                                ) : (
                                  <ExpandMore fontSize="small" />
                                )}
                              </IconButton>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                sx={{ color: 'primary.dark' }}
                              >
                                {groupName}
                              </Typography>
                              <Chip
                                label={`${items.length} ${items.length === 1 ? 'item' : 'items'}`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  color: 'primary.dark',
                                  borderColor: 'primary.300',
                                }}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>

                        {/* Group Data Rows */}
                        {isExpanded &&
                          items.map((item, idx) => {
                            const change = calculateChange(
                              item.bundleCost,
                              item.previousBundleCost
                            );
                            const isNew = change.type === 'new';
                            const isIncrease = change.type === 'increase';
                            return (
                              <TableRow
                                key={item.id}
                                hover
                                sx={{
                                  bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50',
                                  '&:hover': { bgcolor: 'action.hover' },
                                }}
                              >
                                <TableCell sx={{ pl: 5, fontSize: '0.875rem' }}>
                                  {item.size}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ fontSize: '0.875rem', color: 'text.secondary' }}
                                >
                                  {formatCurrency(item.previousBundleCost)}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ fontSize: '0.875rem', fontWeight: 500 }}
                                >
                                  {formatCurrency(item.bundleCost)}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.875rem' }}>
                                  {isNew ? (
                                    <Chip
                                      label="NEW"
                                      size="small"
                                      color="success"
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  ) : change.type === 'no-change' ? (
                                    <Typography
                                      component="span"
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      = No change
                                    </Typography>
                                  ) : (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        color: isIncrease ? 'success.main' : 'error.main',
                                        fontWeight: 500,
                                      }}
                                    >
                                      <span>{isIncrease ? '↑' : '↓'}</span>
                                      <span>
                                        {isIncrease ? '+' : '-'}
                                        {formatCurrency(change.value)} ({isIncrease ? '+' : '-'}
                                        {change.percent.toFixed(1)}%)
                                      </span>
                                    </Box>
                                  )}
                                </TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                                  {item.sticksPerBundle || item.sheetsPerBundle || '—'}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                                  {formatDate(item.updated)}
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title={isDraft && canEditPricing ? 'Edit' : 'View'}>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => {
                                        setSelectedBundleCost(item);
                                        setBundleCostModalMode(
                                          isDraft && canEditPricing ? 'edit' : 'view'
                                        );
                                        setBundleCostModalOpen(true);
                                      }}
                                    >
                                      {isDraft && canEditPricing ? (
                                        <EditIcon fontSize="small" />
                                      ) : (
                                        <VisibilityIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : bomHardwareLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <Typography color="text.secondary">Loading hardware costs...</Typography>
          </Box>
        ) : bomHardware.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <Typography color="text.secondary">
              No hardware costs found.{' '}
              {isDraft && canEditPricing && 'Click "+ Add Hardware Cost" to get started.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '22%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '10%' }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50', pl: 2 }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Size</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>
                    Cost Per Box
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>
                    Quantity
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>
                    # Items Per Box
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>
                    Cost Per Item
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bomHardware.map((item, idx) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{
                      bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <TableCell sx={{ pl: 2, fontSize: '0.875rem', fontWeight: 500 }}>
                      {item.hardwareType}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                      {item.description}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.875rem' }}>
                      {formatCurrency(item.costPerBox)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontSize: '0.875rem', color: 'text.secondary' }}
                    >
                      {(item.quantity ?? 1).toLocaleString()}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontSize: '0.875rem', color: 'text.secondary' }}
                    >
                      {item.itemsPerBox.toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                      {formatCurrency(item.costPerItem)}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={isDraft && canEditPricing ? 'Edit' : 'View'}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setSelectedBOMHardware(item);
                            setBOMHardwareModalMode(isDraft && canEditPricing ? 'edit' : 'view');
                            setBOMHardwareModalOpen(true);
                          }}
                        >
                          {isDraft && canEditPricing ? (
                            <EditIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <BundleCostModal
        open={bundleCostModalOpen}
        onClose={() => setBundleCostModalOpen(false)}
        onSuccess={() => {
          // Grid will refetch automatically via cache invalidation
        }}
        mode={bundleCostModalMode}
        version={version}
        bundleCost={selectedBundleCost}
      />

      <BOMHardwareModal
        open={bomHardwareModalOpen}
        onClose={() => setBOMHardwareModalOpen(false)}
        onSuccess={() => {}}
        mode={bomHardwareModalMode}
        version={version}
        bomHardware={selectedBOMHardware}
      />

      <PublishVersionModal
        open={publishModalOpen}
        version={version}
        manufacturerName={manufacturerName}
        onClose={() => setPublishModalOpen(false)}
        onSuccess={(publishedVersion: PriceVersion) => {
          setPublishModalOpen(false);
          // Navigate to dashboard, passing manufacturer + success toast via router state
          navigate('/product-pricing-calculator', {
            state: {
              publishedManufacturerId: publishedVersion.manufacturerId,
              successMessage: `Version ${publishedVersion.versionNumber}.0 published successfully!`,
            },
          });
        }}
      />

      <BulkUpdateModal
        open={bulkUpdateModalOpen}
        version={version}
        materialGroupOptions={materialGroupOptions}
        bundleCosts={bundleCosts}
        bomHardware={bomHardware}
        onClose={() => setBulkUpdateModalOpen(false)}
        onSuccess={(updatedCount, dataType, cappedCount) => {
          const msg = `${updatedCount} ${dataType} updated successfully.`;
          const cappedMsg =
            cappedCount > 0
              ? ` Note: ${cappedCount} record${cappedCount !== 1 ? 's' : ''} capped at $0.01 minimum.`
              : '';
          setSuccessMessage(msg + cappedMsg);
        }}
      />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductPricingCalculatorPage;
